import React from "react";
import { ShieldCheck, Droplets, Wrench, Move } from "lucide-react";
import { formatDate } from "@/lib/format";

const CHECKS = [
  { key: "authenticity_checked", label: "Authenticity Checked", icon: ShieldCheck },
  { key: "ultrasonically_cleaned", label: "Ultrasonically Cleaned", icon: Droplets },
  { key: "hinges_checked", label: "Hinges Checked", icon: Wrench },
  { key: "alignment_checked", label: "Frame Alignment Checked", icon: Move },
];

/** The premium "certificate of preparation" for one unique, individually-inspected frame. */
export function FramePassport({ product }) {
  if (!product) return null;
  const { lens_width, bridge_width, temple_length } = product;
  const hasMeasurements = lens_width && bridge_width && temple_length;
  const defects = product.imperfections?.length ? product.imperfections.map((im) => (typeof im === "string" ? im : im.note)) : (product.condition_notes ? [product.condition_notes] : []);

  return (
    <div className="relative bg-charcoal rounded-3xl overflow-hidden text-white" data-testid="frame-passport">
      <div className="absolute inset-x-0 top-0 h-1 bg-[#09B8DF]" />
      <div className="p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[#09B8DF] text-xs font-heading font-bold uppercase tracking-[0.2em] mb-1">Lookers Frame Passport</div>
            <div className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight" data-testid="frame-passport-id">
              #{product.inventory_id || product.sku}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-3xl font-mono font-extrabold text-[#09B8DF]">{product.condition_score?.toFixed(1)}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/50">/10 · {product.condition_label}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Brand &amp; Model</div>
            <div className="font-heading font-semibold">{product.brand_name} {product.model}</div>
          </div>
          {product.model_number && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Model Number</div>
              <div className="font-mono">{product.model_number}</div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Colour</div>
            <div>{product.frame_colour}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Authentication</div>
            <div data-testid="frame-passport-authentication">
              {product.authentication_method === "Third-Party Authentication" && product.authentication_provider
                ? `Independently Authenticated · ${product.authentication_provider}`
                : product.authentication_method || "Lookers Authentication Check"}
            </div>
          </div>
          {hasMeasurements && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Measurements</div>
              <div className="font-mono" data-testid="frame-passport-measurements">{lens_width}–{bridge_width}–{temple_length}</div>
            </div>
          )}
        </div>

        {defects.length > 0 && (
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Known Wear / Imperfections</div>
            <ul className="text-sm text-white/80 list-disc list-inside space-y-0.5">
              {defects.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-4 mb-6">
          {CHECKS.filter((c) => product.inspection?.[c.key]).map((c) => (
            <div key={c.key} className="flex items-center gap-1.5 text-xs text-white/80" data-testid={`passport-check-${c.key}`}>
              <c.icon size={14} className="text-[#09B8DF]" />
              {c.label}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-white/10 pt-4 text-xs text-white/50">
          <span>Original Case: {product.original_case_included == null ? "Not Recorded" : product.original_case_included ? "Yes" : "No"}</span>
          {product.inspection_date && <span>Inspected {formatDate(product.inspection_date)}</span>}
        </div>
      </div>
    </div>
  );
}
